import { computed, effect, inject, untracked } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';

import { AppError, GetPaginationRes, SpinnerService } from '@hglobal/fin-ui';
import { tapResponse } from '@ngrx/operators';
import { patchState, signalStore, withComputed, withHooks, withMethods, withProps, withState } from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { switchMap } from 'rxjs';

import { Role, SUPPORT_TELEGRAM_LINK, SupportIssueType } from '@zeusm/business/core/entities';
import type { Rates } from '@zeusm/business/core/entities';
import { PermissionsStore, StorageStore } from '@zeusm/business/core/store';
import { BusinessDictionariesStore } from '@zeusm/business/dictionaries';
import { ProfileService } from '@zeusm/shared/session/profile';
import { setErrorStatusFor, setPendingStatusFor, setSuccessStatusFor, withRequestHandler, withRequestStatus } from '@zeusm/shared/store';

import { SHELL_API } from './api';
import { ApiSettings, Conditions } from './entities';

interface BusinessShellState {
  rates: GetPaginationRes<Rates>;
  cryptoMarket: GetPaginationRes<any>;
  totalBalance: string;
  appSettings: ApiSettings;
  conditions: Conditions[];
  hasAgentAccounts: boolean;
  telegramLink: string;
}

const initialState: BusinessShellState = {
  rates: null,
  conditions: null,
  cryptoMarket: null,
  totalBalance: null,
  appSettings: null,
  hasAgentAccounts: null,
  telegramLink: null,
};

export const BusinessShellStore = signalStore(
  withState(initialState),
  withRequestStatus([
    'getRates',
    'getCryptoMarket',
    'getTotalBalance',
    'getSettings',
    'getConditions',
    'generateTelegramLink',
    'getHasAgentAccounts',
    'redirectToSupport',
  ]),
  withRequestHandler(),
  withProps(store => ({
    store,
    shellApi: inject(SHELL_API),
    dictionariesStore: inject(BusinessDictionariesStore),
    permissions: inject(PermissionsStore),
    spinnerService: inject(SpinnerService),
    storageStore: inject(StorageStore),
    profileService: inject(ProfileService),
  })),
  withMethods(store => ({
    resetGenerateTelegramLink: () => store.resetRequestStatus('generateTelegramLink'),
  })),
  withMethods(({ store, shellApi }) => ({
    getRates: rxMethod<void>(
      switchMap(() => {
        patchState(store, setPendingStatusFor('getRates'));
        return shellApi.getRates().pipe(
          tapResponse({
            next: rates => patchState(store, { rates }, setSuccessStatusFor('getRates')),
            error: (error: AppError) => patchState(store, setErrorStatusFor('getRates', error)),
          }),
        );
      }),
    ),
    getCryptoMarket: rxMethod<void>(
      switchMap(() => {
        patchState(store, setPendingStatusFor('getCryptoMarket'));
        return shellApi.getCryptoMarket().pipe(
          tapResponse({
            next: cryptoMarket => patchState(store, { cryptoMarket }, setSuccessStatusFor('getCryptoMarket')),
            error: (error: AppError) => patchState(store, setErrorStatusFor('getCryptoMarket', error)),
          }),
        );
      }),
    ),
    getTotalBalance: rxMethod<number>(
      switchMap(currencyId => {
        patchState(store, setPendingStatusFor('getTotalBalance'));
        return shellApi.getTotalBalance(currencyId).pipe(
          tapResponse({
            next: totalBalance => patchState(store, { totalBalance }, setSuccessStatusFor('getTotalBalance')),
            error: (error: AppError) => patchState(store, setErrorStatusFor('getTotalBalance', error)),
          }),
        );
      }),
    ),
    getSettings: rxMethod<void>(
      switchMap(() => {
        patchState(store, setPendingStatusFor('getSettings'));
        return shellApi.getSettings().pipe(
          tapResponse({
            next: appSettings => patchState(store, { appSettings }, setSuccessStatusFor('getSettings')),
            error: (error: AppError) => patchState(store, setErrorStatusFor('getSettings', error)),
          }),
        );
      }),
    ),
    getHasAgentAccounts: rxMethod<{ orgId: string }>(
      switchMap(payload => {
        patchState(store, setPendingStatusFor('getHasAgentAccounts'));
        return shellApi.getHasAgentAccounts(payload).pipe(
          tapResponse({
            next: hasAgentAccounts => patchState(store, { hasAgentAccounts }, setSuccessStatusFor('getHasAgentAccounts')),
            error: (error: AppError) => patchState(store, setErrorStatusFor('getHasAgentAccounts', error)),
          }),
        );
      }),
    ),
    getConditions: rxMethod<void>(
      switchMap(() => {
        patchState(store, setPendingStatusFor('getConditions'));
        return shellApi.getConditions().pipe(
          tapResponse({
            next: conditionsRes => patchState(store, { conditions: conditionsRes.sections || [] }, setSuccessStatusFor('getConditions')),
            error: (error: AppError) => patchState(store, setErrorStatusFor('getConditions', error)),
          }),
        );
      }),
    ),
    generateTelegramLink: rxMethod<void>(
      switchMap(() => {
        patchState(store, setPendingStatusFor('generateTelegramLink'));
        return shellApi.generateTelegramLink().pipe(
          tapResponse({
            next: telegramLink => patchState(store, { telegramLink }, setSuccessStatusFor('generateTelegramLink')),
            error: (error: AppError) => patchState(store, setErrorStatusFor('generateTelegramLink', error)),
          }),
        );
      }),
    ),
    redirectToSupport: rxMethod<SupportIssueType>(
      switchMap((issueType: SupportIssueType) => {
        patchState(store, setPendingStatusFor('redirectToSupport'));
        return shellApi.getSupportLink(issueType).pipe(
          tapResponse({
            next: resp => {
              window.open(resp.url, '_blank');
              patchState(store, setSuccessStatusFor('redirectToSupport'));
            },
            error: (error: AppError) => {
              window.open(SUPPORT_TELEGRAM_LINK, '_blank');
              patchState(store, setErrorStatusFor('redirectToSupport', error));
            },
          }),
        );
      }),
    ),
  })),
  withComputed(({ store, permissions }) => ({
    isAgentAccountsFeatureVisible: computed(
      () => permissions.isAgentAccountsVisible() && store.appSettings()?.agentAccounts?.enabled === true,
    ),
  })),
  withHooks({
    onInit(store) {
      const settingsPermissions = store.permissions.settingsPermissions;
      const role = store.permissions.role;
      const isBusinessProfileActive = store.profileService.isBusinessProfileActive;

      store.spinnerService.observe(toObservable(store.isLoading));

      effect(() => {
        if (!isBusinessProfileActive()) return;

        store.getSettings();
      });

      effect(() => {
        if (!isBusinessProfileActive()) return;

        if (store.isAgentAccountsFeatureVisible()) {
          const orgId = store.storageStore.orgId();
          if (orgId) {
            store.getHasAgentAccounts({ orgId });
          }
        }
      });

      effect(() => {
        if (!isBusinessProfileActive()) return;

        const orgId = store.storageStore.orgId();
        if (!orgId) {
          return;
        }

        untracked(() => {
          store.dictionariesStore.clearOrgScopedData();
          store.dictionariesStore.getCardProducts();
          store.dictionariesStore.getSkins();
        });
      });

      effect(() => {
        if (!isBusinessProfileActive()) return;

        if (!settingsPermissions()?.currency_list) {
          return;
        }
        store.dictionariesStore.getCurrencies();
      });

      effect(() => {
        if (!isBusinessProfileActive()) return;

        if (role() === Role.SuperAdmin) {
          return;
        }
        store.getConditions();
      });
    },
  }),
);
